import React, { useEffect, useRef, useState } from "react";
import { CircleX } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FormData {
    company_name: string;
    business_type: string;
    phone: string;
    email: string;
    notes: string;
}

interface FormErrors {
    company_name?: string;
    business_type?: string;
    phone?: string;
    email?: string;
    notes?: string;
}

// Define regex patterns outside the component
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?\d{9,15}$/;
const baseURL = import.meta.env.VITE_ADMIN_URL || "";

const DemoModal = ({ isOpen, onClose }: ModalProps) => {
    const { t, i18n } = useTranslation();
    const modalRef = useRef<HTMLDivElement>(null);
    const isRTL = i18n.language === "ar";

    // State for form data
    const [formData, setFormData] = useState<FormData>({
        company_name: "",
        business_type: "",
        phone: "",
        email: "",
        notes: "",
    });

    // State for form errors
    const [errors, setErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);

    // Validation rules
    const validateField = (name: string, value: string): string => {
        switch (name) {
            case "company_name":
                return value.trim() === ""
                    ? t("freeCopyValidation.companyName")
                    : value.length > 100
                    ? t("contactUs.maxLength", { max: 100 })
                    : "";
            case "business_type":
                return value.trim() === ""
                    ? t("freeCopyValidation.businessField")
                    : value.length > 50
                    ? t("contactUs.maxLength", { max: 50 })
                    : "";
            case "phone":
                return value.trim() === ""
                    ? t("freeCopyValidation.phone")
                    : !PHONE_REGEX.test(value)
                    ? t("contactUs.invalidPhone")
                    : value.length > 15
                    ? t("contactUs.maxLength", { max: 15 })
                    : "";
            case "email":
                return value && !EMAIL_REGEX.test(value)
                    ? t("freeCopyValidation.email2")
                    : value.length > 100
                    ? t("contactUs.maxLength", { max: 100 })
                    : "";
            case "notes":
                return value.length > 500
                    ? t("contactUs.maxLength", { max: 500 })
                    : "";
            default:
                return "";
        }
    };

    const validateForm = (): FormErrors => {
        const newErrors: FormErrors = {};
        Object.keys(formData).forEach((key) => {
            const error = validateField(key, formData[key as keyof FormData]);
            if (error) {
                newErrors[key as keyof FormErrors] = error;
            }
        });
        return newErrors;
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        if (touched[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: validateField(name, value),
            }));
        }
    };

    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setTouched((prev) => ({
            ...prev,
            [name]: true,
        }));
        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, value),
        }));
    };

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    // Clear form data, errors, and touched state when modal is closed
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                company_name: "",
                business_type: "",
                phone: "",
                email: "",
                notes: "",
            });
            setErrors({});
            setTouched({});
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formErrors = validateForm();
        setErrors(formErrors);
        const allTouched = Object.keys(formData).reduce(
            (acc, key) => ({
                ...acc,
                [key]: true,
            }),
            {}
        );
        setTouched(allTouched);
        if (Object.keys(formErrors).length > 0) {
            return;
        }
        setLoading(true);
        try {
            await axios.post(
                `${baseURL}/tenants/api/demo-request/`,
                formData
            );
            toast.success(t(`contactUs.toaster.success`));
            onClose(); // Form will be cleared by useEffect when modal closes
        } catch (error) {
            console.error("Error submitting form:", error);
            toast.error(t(`toaster.failed`));
        } finally {
            setLoading(false);
        }
    };
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[9999]">
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300"
                onClick={handleBackdropClick}
            />

            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg px-4">
                <div
                    ref={modalRef}
                    className="card shadow-2xl w-full transform transition-all duration-300 scale-100"
                >
                    <div className="p-8">
                        <div className="mb-8 flex justify-between items-center">
                            <div>
                                <h2 className="text-2xl font-bold text-text mb-2">
                                    {t("freeCopy.heading")}
                                </h2>
                                <div className="w-12 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                            </div>
                            <button
                                className="p-2 text-text/60 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200 hover:scale-110"
                                onClick={onClose}
                                aria-label="Close modal"
                            >
                                <CircleX className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6" dir={isRTL ? "rtl" : "ltr"}>
                            <div>
                                <input
                                    type="text"
                                    name="company_name"
                                    placeholder={t("freeCopy.companyName")}
                                    value={formData.company_name}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={`input-field ${
                                        errors.company_name && touched.company_name
                                            ? "border-red-500 focus:ring-red-500"
                                            : ""
                                    }`}
                                />
                                {errors.company_name && touched.company_name && (
                                    <p className="text-red-500 text-sm mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.company_name}
                                    </p>
                                )}
                            </div>
                            
                            <div>
                                <select
                                    name="business_type"
                                    value={formData.business_type}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={`input-field appearance-none bg-white text-text ${
                                        errors.business_type && touched.business_type
                                            ? "border-red-500 focus:ring-red-500"
                                            : ""
                                    } ${!formData.business_type ? 'text-text/50' : 'text-text'}`}
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                                        backgroundPosition: `${isRTL ? 'left 0.75rem' : 'right 0.75rem'} center`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '1.5em 1.5em',
                                        paddingRight: isRTL ? '1rem' : '2.5rem',
                                        paddingLeft: isRTL ? '2.5rem' : '1rem',
                                    }}
                                >
                                    <option value="" disabled style={{ color: '#9CA3AF' }}>
                                        {t("freeCopy.businessField")}
                                    </option>
                                    <option value="IT" style={{ color: '#2C3E50' }}>
                                        {t("freeCopy.it")}
                                    </option>
                                    <option value="Healthcare" style={{ color: '#2C3E50' }}>
                                        {t("freeCopy.healthcare")}
                                    </option>
                                    <option value="Finance" style={{ color: '#2C3E50' }}>
                                        {t("freeCopy.finance")}
                                    </option>
                                    <option value="Retail" style={{ color: '#2C3E50' }}>
                                        {t("freeCopy.retail")}
                                    </option>
                                    <option value="Education" style={{ color: '#2C3E50' }}>
                                        {t("freeCopy.education")}
                                    </option>
                                    <option value="Other" style={{ color: '#2C3E50' }}>
                                        {t("freeCopy.other")}
                                    </option>
                                </select>
                                {errors.business_type && touched.business_type && (
                                    <p className="text-red-500 text-sm mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.business_type}
                                    </p>
                                )}
                            </div>
                            
                            <div>
                                <input
                                    type="text"
                                    name="phone"
                                    placeholder={t("freeCopy.phone")}
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={`input-field ${
                                        errors.phone && touched.phone
                                            ? "border-red-500 focus:ring-red-500"
                                            : ""
                                    }`}
                                />
                                {errors.phone && touched.phone && (
                                    <p className="text-red-500 text-sm mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.phone}
                                    </p>
                                )}
                            </div>
                            
                            <div>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder={t("freeCopy.email")}
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={`input-field ${
                                        errors.email && touched.email
                                            ? "border-red-500 focus:ring-red-500"
                                            : ""
                                    }`}
                                />
                                {errors.email && touched.email && (
                                    <p className="text-red-500 text-sm mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.email}
                                    </p>
                                )}
                            </div>
                            
                            <div>
                                <textarea
                                    name="notes"
                                    placeholder={t("freeCopy.notes")}
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    rows={3}
                                    className={`input-field text-text resize-none ${
                                        errors.notes && touched.notes
                                            ? "border-red-500 focus:ring-red-500"
                                            : ""
                                    }`}
                                />
                                {errors.notes && touched.notes && (
                                    <p className="text-red-500 text-sm mt-2 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.notes}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                {loading ? (
                                    <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                ) : null}
                                {loading ? t("freeCopy.sending") : t("freeCopy.sendRequest")}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DemoModal;
